import { useState, useRef, useEffect, useMemo } from "react";
import IconButton from "@mui/material/IconButton";
import {
  CometChatConversations,
  CometChatUsers,
  CometChatUIKit,
  CometChatMessageList,
  CometChatMessageComposer,
  CometChatMessageHeader,
  CometChatGroupEvents,
  IMessages,
  CometChatMessageEvents,
} from "@cometchat/chat-uikit-react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { ReactComponent as UpArrow } from "../../assets/Icons/arrow-up.svg";
import { ReactComponent as DownArrow } from "../../assets/Icons/arrow-down.svg";
import useDraftSync from "../../hooks/useDraftSync";
import useFloatingChatDrafts from "../../hooks/useFloatingChatDrafts";
import useResize from "../../hooks/useResize";
import { trackClickEvent } from "../../mixpanel";
import "./style.css";

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSearchUser, setSelectedSearchUser] = useState(null);
  const [conversationKey, setConversationKey] = useState(0);
  const [activeTab, setActiveTab] = useState("chats");
  const chatRef = useRef(null);
  const messageRef = useRef(null);
  const isMobileView = useResize(600);
  
  // Use specialized draft hook for FloatingChat
  useFloatingChatDrafts();
  
  // Use draft sync hook for cross-component synchronization
  useDraftSync();

  // Create conversation request builder that only shows user conversations (no groups)
  const conversationsRequestBuilder = useMemo(() => {
    return new CometChat.ConversationsRequestBuilder()
      .setLimit(30)
      .withBlockedInfo(true)
      .setConversationType("user");
  }, []);

  useEffect(() => {
    CometChatUIKit.getLoggedinUser().then((user) => {
      setLoggedInUser(user);
    });

    const groupCreated = CometChatGroupEvents.ccGroupCreated.subscribe(
      (group) => {
        setConversationKey((prev) => prev + 1);
      }
    );

    return () => {
      groupCreated?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      trackClickEvent('Chat Interface Opened', { source: 'Floating Chat' });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatRef.current &&
        !chatRef.current.contains(event.target) &&
        messageRef.current &&
        !messageRef.current.contains(event.target)
      ) {
        setTimeout(() => {
          setIsOpen(false);
          handleCloseMessages();
        }, 0);
      }
    };

    if (isOpen || selectedConversation || selectedUser || showNewChat) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, selectedConversation, selectedUser, showNewChat]);


  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    setSelectedUser(null);
    setShowNewChat(false);
    setSelectedSearchUser(null);
    setMessagesLoaded(true);
  };


  // Scroll to bottom when messages load
  useEffect(() => {
    if (
      (selectedConversation || selectedUser) &&
      messagesLoaded &&
      messageRef.current
    ) {
      const scrollToBottom = () => {
        // Find the wrapper and scroll it
        const wrapper = messageRef.current?.querySelector(
          ".message-list-wrapper"
        );
        if (wrapper) {
          wrapper.scrollTop = wrapper.scrollHeight;
        }

        // Also try to find and scroll the CometChat message list
        const messageList = messageRef.current?.querySelector(
          '[class*="cometchat-message-list"]'
        );
        if (messageList) {
          messageList.scrollTop = messageList.scrollHeight;
        }
      };

      // Try multiple times with increasing delays
      const timeouts = [100, 500, 1000, 1500, 2000].map((delay) =>
        setTimeout(scrollToBottom, delay)
      );

      return () => timeouts.forEach((t) => clearTimeout(t));
    }
  }, [selectedConversation, selectedUser, messagesLoaded]);

  const handleCreateNewChat = () => {
    setShowNewChat(true);
    setSelectedConversation(null);
    setSelectedUser(null);
    setSelectedSearchUser(null);
    setSearchQuery("");
    setSearchResults([]);
    setMessagesLoaded(true);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim() === "") {
      setSearchResults([]);
      return;
    }

    try {
      const usersRequest = new CometChat.UsersRequestBuilder()
        .setLimit(20)
        .setSearchKeyword(query)
        .build();

      const users = await usersRequest.fetchNext();
      setSearchResults(users);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    }
  };

  const handleSearchUserClick = async (user) => {
    try {
      const conversationsRequest = new CometChat.ConversationsRequestBuilder()
        .setLimit(50)
        .withBlockedInfo(true)
        .build();

      const conversations = await conversationsRequest.fetchNext();
      const existingConversation = conversations.find(
        (conv) =>
          conv.getConversationType() === "user" &&
          conv.getConversationWith().getUid() === user.uid
      );

      if (existingConversation) {
        setSelectedConversation(existingConversation);
        setShowNewChat(false);
        setSelectedSearchUser(null);
        setMessagesLoaded(true);

      } else {
        trackClickEvent('New 1:1 Conversation Started', { 
          source: 'Floating Chat',
          receiver_id: user.uid,
          receiver_name: user.name
        });
        setSelectedUser(user);
        setSelectedConversation(null);
        setShowNewChat(false);
        setSelectedSearchUser(null);
        setMessagesLoaded(true);
        setConversationKey((prev) => prev + 1);

      }

      setSearchResults([]);
      setSearchQuery("");
    } catch (error) {
      console.error("Error checking existing conversations:", error);
      setSelectedUser(user);
      setSelectedConversation(null);
      setShowNewChat(false);
      setSelectedSearchUser(null);
      setMessagesLoaded(true);
      setSearchResults([]);
      setSearchQuery("");
    }
  };

  const handleCloseMessages = () => {
    setSelectedConversation(null);
    setSelectedUser(null);
    setShowNewChat(false);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedSearchUser(null);
    setMessagesLoaded(false);
  };

  // Hide floating chat on mobile/tablet screens
  if (isMobileView) {
    return null;
  }

  return (
    <>
      <div className="floating-chat" ref={chatRef}>
        {/* LinkedIn-style Chat Toggle Bar */}
        <div
          className="floating-chat-toggle"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="floating-chat-toggle-content">
            <span className="floating-chat-toggle-text">{"Messages"}</span>
            <span className="floating-chat-toggle-icon">
              <IconButton size="small">
                {!isOpen ? (
                  <UpArrow fontSize="small" />
                ) : (
                  <DownArrow fontSize="small" />
                )}
              </IconButton>
            </span>
          </div>
        </div>

        {/* Chat Widget */}
        <div className={`floating-chat-widget ${isOpen ? "open" : ""}`}>
          <div className="floating-chat-header">
            <h3>Messages</h3>
            <button
              className="floating-chat-close"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>

          <div
            className="floating-chat-content"
            style={{ position: "relative" }}
          >
            {/* Tab Navigation */}
            <div className="floating-chat-tabs">
              <button
                className={`floating-chat-tab ${
                  activeTab === "chats" ? "active" : ""
                }`}
                onClick={() => setActiveTab("chats")}
              >
                Messages
              </button>
              <button
                className={`floating-chat-tab ${
                  activeTab === "users" ? "active" : ""
                }`}
                onClick={() => setActiveTab("users")}
              >
                People
              </button>
            </div>

            {/* Tab Content */}
            <div className="floating-chat-tab-content">
              {activeTab === "chats" && (
                <CometChatConversations
                  key={conversationKey}
                  onItemClick={handleConversationClick}
                  headerView={<></>}
                  conversationsRequestBuilder={conversationsRequestBuilder}
                />
              )}
              {activeTab === "users" && (
                <CometChatUsers
                  onItemClick={(user) => {
                    trackClickEvent('New 1:1 Conversation Started', { 
                      source: 'Floating Chat',
                      receiver_id: user.uid,
                      receiver_name: user.name
                    });
                    setSelectedUser(user);
                    setSelectedConversation(null);
                    setShowNewChat(false);
                    setMessagesLoaded(true);
                  }}
                  headerView={<></>}
                />
              )}
            </div>
            {/* Floating Create Chat Button */}
            <button
              onClick={handleCreateNewChat}
              style={{
                position: "absolute",
                bottom: "20px",
                right: "20px",
                width: "50px",
                height: "50px",
                backgroundColor: "#F1832C",
                color: "white",
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                boxShadow: "0 4px 12px rgba(0, 123, 255, 0.3)",
                transition: "all 0.2s ease",
                zIndex: 10,
              }}
              title="Create new chat"
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.1)";
                e.target.style.boxShadow = "0 6px 16px rgba(0, 123, 255, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 4px 12px rgba(0, 123, 255, 0.3)";
              }}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Message Widget - Rendered as separate fixed element */}
      {(((selectedConversation || selectedUser) && messagesLoaded) ||
        showNewChat) && (
        <div className="floating-message-widget-portal" ref={messageRef}>
          <div className="floating-chat-header">
            <h3>{showNewChat ? "New Message" : ""}</h3>
            <button
              className="floating-chat-close"
              onClick={handleCloseMessages}
            >
              ×
            </button>
          </div>

          <div className="floating-chat-content">
            {showNewChat ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div
                  style={{ padding: "15px", borderBottom: "1px solid #e0e0e0" }}
                >
                  <input
                    type="text"
                    placeholder="Search people"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                </div>

                <div style={{ flex: 1, overflow: "auto" }}>
                  {searchResults.length > 0 ? (
                    <div style={{ padding: "10px" }}>
                      {searchResults.map((user) => (
                        <div
                          key={user.uid}
                          onClick={() => handleSearchUserClick(user)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            backgroundColor:
                              selectedSearchUser?.uid === user.uid
                                ? "#e3f2fd"
                                : "transparent",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              backgroundColor: "#007bff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "16px",
                              marginRight: "12px",
                            }}
                          >
                            {user.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div>
                            <div
                              style={{ fontWeight: "500", fontSize: "14px" }}
                            >
                              {user.name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : searchQuery ? (
                    <div
                      style={{
                        padding: "40px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      No users found for "{searchQuery}"
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "40px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      Type a name to search for people
                    </div>
                  )}
                </div>
              </div>
            ) : selectedConversation || selectedUser ? (
              <div className="floating-chat-messages">
                <CometChatMessageHeader
                  user={
                    selectedConversation?.getConversationType() === "user"
                      ? selectedConversation.getConversationWith()
                      : selectedUser
                  }
                  disableVoiceCall={true}
                  disableVideoCall={true}
                />
                <div
                  className="message-list-wrapper"
                  style={{ flex: "1 1 auto", overflow: "auto", minHeight: 0 }}
                >
                  <CometChatMessageList
                    user={
                      selectedConversation?.getConversationType() === "user"
                        ? selectedConversation.getConversationWith()
                        : selectedUser
                    }
                  />
                </div>
                <div style={{ position: "relative" }}>
                  <CometChatMessageComposer
                    user={
                      selectedConversation?.getConversationType() === "user"
                        ? selectedConversation.getConversationWith()
                        : selectedUser
                    }
                    disableVoiceCall={true}
                    disableVideoCall={true}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChat;
